import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["vinInput", "decodeButton", "status", "statusContent"]

  connect() {
    this.setupFormTargets()
  }

  validateVin() {
    const vin = this.vinInputTarget.value.trim().toUpperCase()
    const isValid = this.isValidVin(vin)
    
    this.decodeButtonTarget.disabled = !isValid
    
    if (vin.length === 17 && !isValid) {
      this.showStatus('error', 'Invalid VIN format. Please check your entry.')
    } else if (this.statusTarget.classList.contains('block')) {
      this.hideStatus()
    }
  }

  async decodeVin() {
    const vin = this.vinInputTarget.value.trim().toUpperCase()
    
    if (!this.isValidVin(vin)) {
      this.showStatus('error', 'Please enter a valid 17-character VIN.')
      return
    }

    this.showStatus('loading', 'Decoding VIN and looking up specifications...')
    this.decodeButtonTarget.disabled = true

    try {
      const response = await fetch('/coaches/decode_vin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ vin: vin })
      })

      const data = await response.json()

      if (data.success) {
        this.populateForm(data)
        this.showStatus('success', `Successfully decoded VIN! Found ${data.make} ${data.model} ${data.year}. Form fields have been populated with available information.`)
      } else {
        this.showStatus('error', data.error || 'Failed to decode VIN. Please try again or fill out the form manually.')
      }
    } catch (error) {
      console.error('VIN decode error:', error)
      this.showStatus('error', 'Network error occurred. Please check your connection and try again.')
    } finally {
      this.decodeButtonTarget.disabled = false
    }
  }

  populateForm(data) {
    // Basic information
    this.setFieldValue('year', data.year)
    this.setFieldValue('make', data.make)
    this.setFieldValue('model', data.model || data.coach_specific?.series)
    this.setFieldValue('mileage', '') // Clear mileage as VIN doesn't contain this

    // Engine information
    if (data.engine_manufacturer) {
      this.setFieldValue('engine_make', data.engine_manufacturer)
    }
    if (data.engine_model) {
      this.setFieldValue('engine_model', data.engine_model)
    }

    // Transmission
    if (data.transmission_style && data.transmission_speeds) {
      this.setFieldValue('transmission', `${data.transmission_speeds}-speed ${data.transmission_style}`)
    }

    // Dimensions from coach-specific data
    if (data.coach_specific?.length_feet) {
      this.setFieldValue('length_feet', data.coach_specific.length_feet)
    }

    // Technical specifications from Prevost data
    if (data.coach_specific?.typical_gvwr) {
      // Extract numeric value from GVWR string like "42,540 lbs"
      const gvwrMatch = data.coach_specific.typical_gvwr.match(/[\d,]+/)
      if (gvwrMatch) {
        this.setFieldValue('gross_weight', gvwrMatch[0].replace(/,/g, ''))
      }
    }

    // Fuel capacity
    if (data.coach_specific?.fuel_capacity) {
      const fuelMatch = data.coach_specific.fuel_capacity.match(/\d+/)
      if (fuelMatch) {
        this.setFieldValue('fuel_capacity', fuelMatch[0])
      }
    }

    // Set description with technical details
    this.populateDescription(data)

    // Set chassis number from shell number
    if (data.coach_specific?.shell_number) {
      this.setFieldValue('chassis_number', data.coach_specific.shell_number)
    }

    const basicsHeader = document.querySelector('.bg-gray-50 h3') || this.element.closest('form')
    basicsHeader?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  populateDescription(data) {
    const descriptionParts = []
    
    // Add coach type and series info
    if (data.coach_specific?.coach_type && data.coach_specific?.series) {
      descriptionParts.push(`${data.coach_specific.series} ${data.coach_specific.coach_type}`)
    }

    // Add engine info
    const engineParts = []
    if (data.engine_displacement_l) {
      engineParts.push(`${data.engine_displacement_l}L`)
    }
    if (data.engine_cylinders) {
      engineParts.push(`${data.engine_cylinders} cylinder`)
    }
    if (data.fuel_type) {
      engineParts.push(data.fuel_type.toLowerCase())
    }
    if (engineParts.length > 0) {
      descriptionParts.push(`Engine: ${engineParts.join(', ')}`)
    }

    // Add technical specs
    const techSpecs = []
    if (data.coach_specific?.typical_gvwr) {
      techSpecs.push(`GVWR: ${data.coach_specific.typical_gvwr}`)
    }
    if (data.wheelbase || data.coach_specific?.wheelbase) {
      const wheelbase = data.wheelbase || data.coach_specific.wheelbase
      techSpecs.push(`Wheelbase: ${wheelbase}${data.wheelbase ? ' inches' : ''}`)
    }
    if (techSpecs.length > 0) {
      descriptionParts.push(techSpecs.join(', '))
    }

    // Set the description if we have content
    if (descriptionParts.length > 0) {
      const currentDescription = this.getFieldValue('description')
      const newDescription = descriptionParts.join('. ')
      
      if (currentDescription.trim() === '') {
        this.setFieldValue('description', newDescription)
      } else if (!currentDescription.toLowerCase().includes('engine') && 
                 !currentDescription.toLowerCase().includes('gvwr')) {
        this.setFieldValue('description', `${currentDescription}. ${newDescription}`)
      }
    }
  }

  setFieldValue(fieldName, value) {
    if (!value) return
    
    const field = this.findFormField(fieldName)
    if (field && field.value.trim() === '') {
      field.value = value
      // Trigger any change events
      field.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  getFieldValue(fieldName) {
    const field = this.findFormField(fieldName)
    return field ? field.value : ''
  }

  findFormField(fieldName) {
    return this.element.closest('form').querySelector(`[name$="[${fieldName}]"]`)
  }

  setupFormTargets() {
    // Store references to commonly used form fields
    this.formFields = {}
    const form = this.element.closest('form')
    if (form) {
      const fieldNames = [
        'year', 'make', 'model', 'mileage', 'engine_make', 'engine_model',
        'transmission', 'length_feet', 'gross_weight', 'fuel_capacity',
        'description', 'chassis_number'
      ]
      
      fieldNames.forEach(fieldName => {
        const field = form.querySelector(`[name$="[${fieldName}]"]`)
        if (field) {
          this.formFields[fieldName] = field
        }
      })
    }
  }

  showStatus(type, message) {
    this.statusTarget.classList.remove('hidden')
    this.statusTarget.classList.add('block')
    
    // Clear previous classes
    this.statusTarget.classList.remove('bg-green-50', 'border-green-200', 'bg-red-50', 'border-red-200', 'bg-blue-50', 'border-blue-200')
    
    let iconClass = ''
    let textClass = ''
    
    switch (type) {
      case 'success':
        this.statusTarget.classList.add('bg-green-50', 'border-green-200')
        iconClass = 'fas fa-check-circle text-green-500'
        textClass = 'text-green-800'
        break
      case 'error':
        this.statusTarget.classList.add('bg-red-50', 'border-red-200')
        iconClass = 'fas fa-exclamation-circle text-red-500'
        textClass = 'text-red-800'
        break
      case 'loading':
        this.statusTarget.classList.add('bg-blue-50', 'border-blue-200')
        iconClass = 'fas fa-spinner fa-spin text-blue-500'
        textClass = 'text-blue-800'
        break
    }
    
    this.statusContentTarget.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <i class="${iconClass}"></i>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium ${textClass}">${message}</p>
        </div>
      </div>
    `
  }

  hideStatus() {
    this.statusTarget.classList.add('hidden')
    this.statusTarget.classList.remove('block')
  }

  isValidVin(vin) {
    // Basic VIN validation - 17 characters, no I, O, Q
    return /^[A-HJ-NPR-Z0-9]{17}$/.test(vin)
  }
}